import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

const FIELD_BASE =
  'field-shell h-10 md:h-9';

function stateClass(error?: string): string {
  return error ? 'border-critical' : '';
}

export interface FieldProps {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  /** 单位后缀，例如 g / ¥ / A */
  unit?: ReactNode;
  children: (props: { id: string; 'aria-invalid': boolean; className: string }) => ReactNode;
}

/** 统一的「标签 + 控件 + 单位 + 错误/提示」结构。 */
export function Field({ label, error, hint, required, unit, children }: FieldProps) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label ? (
        <label htmlFor={id} className="flex items-baseline justify-between gap-2 text-xs font-medium text-fg-muted">
          <span>
            {label}
            {required ? <span className="ml-0.5 text-critical">*</span> : null}
          </span>
          {unit ? <span className="text-[11px] font-normal text-fg-subtle">{unit}</span> : null}
        </label>
      ) : null}
      {children({ id, 'aria-invalid': Boolean(error), className: `${FIELD_BASE} ${stateClass(error)}` })}
      {error ? (
        <p className="text-xs text-critical">{error}</p>
      ) : hint ? (
        <p className="text-[11px] leading-relaxed text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  unit?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, unit, required, className = '', ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} unit={unit} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <input
          ref={ref}
          id={id}
          className={`${fieldClass} ${className}`}
          required={required}
          {...aria}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  unit?: ReactNode;
}

/** 数值输入：`inputMode="decimal"` 让移动端直接弹数字键盘。 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { label, error, hint, unit, required, className = '', ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} unit={unit} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={`${fieldClass} tabular ${className}`}
          required={required}
          {...aria}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, required, className = '', rows = 3, ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className={`${fieldClass} h-auto py-2 ${className}`}
          required={required}
          {...aria}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  options: readonly { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, required, options, placeholder, className = '', ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <select
          ref={ref}
          id={id}
          className={`${fieldClass} cursor-pointer ${className}`}
          required={required}
          {...aria}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

export function Switch({ checked, onChange, label, hint, disabled }: SwitchProps) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          'relative h-6 w-11 shrink-0 rounded-full border transition-colors',
          checked ? 'border-accent bg-accent' : 'border-line-strong bg-surface-3',
          disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        ].join(' ')}
      >
        <span
          aria-hidden
          className={[
            'absolute top-1/2 left-0.5 size-4.5 -translate-y-1/2 rounded-full bg-surface-1 transition-transform',
            checked ? 'translate-x-5' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
      {label ? (
        <label htmlFor={id} className="cursor-pointer text-sm text-fg">
          {label}
          {hint ? <span className="mt-0.5 block text-xs text-fg-subtle">{hint}</span> : null}
        </label>
      ) : null}
    </div>
  );
}
